/**
 * Spec 027 — TraceUp: cliente de captura de errores del frontend.
 *
 * Única entrada de reporte (contracts/internal-contracts.md):
 *  - no-op si !VITE_TRACEUP_ENABLED || !VITE_TRACEUP_PROJECT_HASH.
 *  - fire-and-forget: reportError() no bloquea; fetch keepalive con timeout 3s
 *    y .catch() silencioso (NUNCA re-report → anti-cascada).
 *  - scrubber inevadible (piiScrubber) + throttle por firma.
 *  - contexto de operador por canal confiable (JWT + sede activa), research D6.
 */
import { jwtDecode } from "jwt-decode";
import { APP_VERSION } from "@/models/version";
import {
  TraceUpEnvironment,
  TraceUpEvent,
  TraceUpOperatorContext,
  TraceUpSource,
} from "./traceup.types";
import { djb2, scrubText, scrubValue, truncate } from "./piiScrubber";

const MAX_MESSAGE_LEN = 2000;
const MAX_STACK_LEN = 8000;
const TIMEOUT_MS = 3000;
const THROTTLE_MAX = 5;
const THROTTLE_WINDOW_MS = 60000;

const ENABLED =
  String(import.meta.env.VITE_TRACEUP_ENABLED ?? "") === "true";
const HASH = String(import.meta.env.VITE_TRACEUP_PROJECT_HASH ?? "");
const BASE_URL = String(
  import.meta.env.VITE_TRACEUP_BASE_URL ?? "https://traceup.lilab.pe"
);
const ENVIRONMENT = String(
  import.meta.env.VITE_TRACEUP_ENVIRONMENT ?? "PRODUCTION"
) as TraceUpEnvironment;

const isActive = (): boolean => ENABLED && !!HASH;

interface ReportErrorInput {
  error: unknown;
  source: TraceUpSource;
  statusCode?: number;
  url?: string;
  extraMetadata?: Record<string, unknown>;
}

// ── Throttle por firma (estado en memoria de la pestaña) ────────────────────
const throttleMap = new Map<string, { count: number; windowStart: number }>();

function throttle(signature: string): number | null {
  const now = Date.now();
  const entry = throttleMap.get(signature);
  if (!entry || now - entry.windowStart > THROTTLE_WINDOW_MS) {
    const suppressed = entry ? Math.max(0, entry.count - THROTTLE_MAX) : 0;
    throttleMap.set(signature, { count: 1, windowStart: now });
    return suppressed;
  }
  if (entry.count >= THROTTLE_MAX) {
    entry.count += 1;
    return null;
  }
  entry.count += 1;
  return 0;
}

// ── Contexto de operador (canal confiable) ──────────────────────────────────
function buildOperatorContext(): {
  context: TraceUpOperatorContext;
  userId?: string;
} {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return { context: { authenticated: false } };
    const claims = jwtDecode<Record<string, any>>(token);

    // Sede ACTIVA (multi-sede): selectedLocal manda sobre el claim del token.
    let localId: number | undefined = claims?.idlocal
      ? Number(claims.idlocal)
      : undefined;
    try {
      const selected = JSON.parse(localStorage.getItem("selectedLocal") || "{}");
      if (selected?.id) localId = Number(selected.id);
    } catch {
      /* ignore */
    }

    const isAdmin =
      claims?.isAdmin === true ||
      String(claims?.isAdmin).toLowerCase() === "true";

    return {
      userId: claims?.idempleado != null ? String(claims.idempleado) : undefined,
      context: {
        authenticated: true,
        companyId: claims?.idempresa != null ? Number(claims.idempresa) : undefined,
        localId,
        email: claims?.unique_name || undefined,
        isAdmin,
      },
    };
  } catch {
    return { context: { authenticated: false } };
  }
}

function operatorMetadata(ctx: TraceUpOperatorContext): Record<string, unknown> {
  return {
    authenticated: ctx.authenticated,
    ...(ctx.companyId != null ? { companyId: ctx.companyId } : {}),
    ...(ctx.localId != null ? { localId: ctx.localId } : {}),
    ...(ctx.email ? { email: ctx.email } : {}),
    ...(ctx.isAdmin != null ? { isAdmin: ctx.isAdmin } : {}),
  };
}

function extractError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: error.message, stack: error.stack };
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

function buildSignature(message: string, url?: string, stack?: string): string {
  const firstMsgLine = message.split("\n")[0];
  const firstFrame = stack
    ? (stack.split("\n").find((l) => l.trim().startsWith("at")) || "").trim()
    : "";
  return djb2(`ERROR|${firstMsgLine}|${url || ""}|${firstFrame}`);
}

/** Reporte fire-and-forget. NUNCA lanza. */
export function reportError(input: ReportErrorInput): void {
  try {
    if (!isActive()) return;

    const { message, stack } = extractError(input.error);
    const safeMessage = truncate(scrubText(message) || "Unknown error", MAX_MESSAGE_LEN);
    const safeStack = stack ? truncate(scrubText(stack), MAX_STACK_LEN) : undefined;
    const url = input.url || window.location.href;

    const signature = buildSignature(safeMessage, url, stack);
    const suppressed = throttle(signature);
    if (suppressed === null) return;

    const { context, userId } = buildOperatorContext();
    const scrubbedExtra =
      (input.extraMetadata
        ? (scrubValue(input.extraMetadata) as Record<string, unknown>)
        : {}) || {};

    const event: TraceUpEvent = {
      type: "ERROR",
      message: safeMessage,
      environment: ENVIRONMENT,
      ...(safeStack ? { stackTrace: safeStack } : {}),
      userAgent: navigator.userAgent,
      url,
      ...(userId ? { userId } : {}),
      metadata: {
        ...scrubbedExtra,
        source: input.source,
        signature,
        appVersion: APP_VERSION,
        ...(suppressed > 0 ? { suppressedCount: suppressed } : {}),
        ...(typeof input.statusCode === "number"
          ? { statusCode: input.statusCode }
          : {}),
        ...operatorMetadata(context),
      },
    };

    void send(event);
  } catch {
    // el emisor jamás afecta la app ni se auto-reporta
  }
}

function send(event: TraceUpEvent): void {
  try {
    fetch(`${BASE_URL}/api/events/${HASH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      keepalive: true,
      signal: AbortSignal.timeout(TIMEOUT_MS),
    }).catch(() => {
      /* silencio absoluto: nunca reintenta ni re-reporta */
    });
  } catch {
    /* AbortSignal.timeout no soportado u otro fallo → ignorar */
  }
}

// ── Captadores globales (window) ────────────────────────────────────────────
let initialized = false;

/** Registra los handlers globales una sola vez. Llamar en main.tsx. */
export function initTraceUp(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (e: ErrorEvent) => {
    reportError({
      error: e.error ?? e.message,
      source: "frontend-window",
      extraMetadata: { filename: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    reportError({ error: e.reason, source: "frontend-window" });
  });
}
